import { LiveBadge } from '@components/live/LiveBadge';
import type { LiveState } from '@models/api';
import { liveLink, liveLinkDisplay } from '@lib/livePolls/liveLinks';
import { DAWN, type LiveScreenColor } from '@lib/livePolls/liveScreenLook';
import { LIVE_POLL_COPY } from '../livePollCopy';
import { BigScreenQr, PlaceLogo, ReconnectingChip, TypedLink } from './BigScreenParts';

export interface BigScreenJoinProps {
  state: LiveState;
  reconnecting?: boolean;
  /** The host's colour; Dawn, the frames' backdrop, without one. */
  color?: LiveScreenColor;
}

/**
 * B1 (before Start: "Starting soon") and B1b (after Start, before question 1: "Get ready" and "Question 1
 * is coming up", the Live pill, RESOLUTIONS F3, L12). Styled after the Activation Kit's QR screen
 * (`src/screens/kit/drawQrScreen.ts`, a visual reference only): the place's logo large, the eyebrow
 * (uppercase by CSS, L10), "Scan to participate", the typed link under the QR (L11).
 */
export function BigScreenJoin({ state, reconnecting = false, color = DAWN }: BigScreenJoinProps) {
  const started = state.live_state === 'live';
  const display = liveLinkDisplay(state.live_code);
  return (
    <div
      className="relative flex h-full w-full items-center justify-between overflow-hidden px-[101px]"
      style={{
        background: color.background,
        color: color.textColor,
      }}
      data-testid="big-screen-join"
    >
      {started || reconnecting ? (
        <div className="absolute left-[101px] top-12 flex items-center gap-5">
          {started ? <LiveBadge size="screen" /> : null}
          {reconnecting ? <ReconnectingChip /> : null}
        </div>
      ) : null}

      <div className="flex w-[560px] min-w-0 flex-col gap-7 [&>*]:shrink-0">
        <div className="flex items-center gap-6">
          <PlaceLogo
            name={state.place.name}
            imageUrl={state.place.image_url}
            className="h-[120px] w-[120px] rounded-[28px]"
            initialsClassName="text-[48px] leading-[56px]"
          />
          <div className="flex min-w-0 flex-col gap-1.5">
            <p className="line-clamp-2 break-words font-livvic text-[40px] font-bold leading-[44px]">
              {state.place.name}
            </p>
            <p className="font-fira text-[20px] font-semibold uppercase leading-6 tracking-[0.08em]">
              {LIVE_POLL_COPY.eyebrowQuestions(state.questions_count)}
            </p>
          </div>
        </div>
        <h1 className="font-livvic text-[104px] font-bold leading-[108px] tracking-[-0.02em]">
          {LIVE_POLL_COPY.scanToParticipate}
        </h1>
      </div>

      <div className="relative flex h-[480px] w-[480px] shrink-0 flex-col items-center justify-center gap-3 rounded-[32px] bg-white p-5 text-[#0D0D0D]">
        <BigScreenQr link={liveLink(state.live_code)} size={392} />
        <p className="text-center font-fira text-[22px] font-semibold leading-7">
          <TypedLink display={display} />
        </p>
      </div>

      <div
        className={
          started
            ? 'absolute bottom-12 left-[101px] flex items-end gap-7'
            : 'absolute bottom-[76px] left-[101px] flex items-center gap-7'
        }
        data-testid="big-screen-status"
      >
        <div className="flex flex-col font-fira text-[24px] font-medium leading-7">
          {started ? (
            <>
              <span className="font-semibold">{LIVE_POLL_COPY.getReady}</span>
              <span>{LIVE_POLL_COPY.questionOneComingUp}</span>
            </>
          ) : (
            <span>{LIVE_POLL_COPY.startingSoon}</span>
          )}
        </div>
        <span className="pl-6 font-livvic text-[26px] font-bold leading-7">
          {LIVE_POLL_COPY.wordmark}
        </span>
      </div>
    </div>
  );
}
